import React from 'react';
import { useLocale } from '@adsk/alloy-react-helpers';
import { useStore } from '@common/helpers/storeProvider';
import { formatDate } from '@common/utils';
import CategoryWrapper from '../CategoryWrapper';
import AddressField from '../Fields/AddressField';
import DateField from '../Fields/DateField';
import TextField from '../Fields/TextField';
import { pickAddressProps } from '../store/utils';
import { MAPBOX_TOKEN, CategorizedContentStoreContext } from '../../constants';
import { initialState } from '../store/reducer';
import { computeDateDiffDays, createDateFromISOString } from '../utils';
import {
  setStartDate,
  setEndDate,
  setProjectName,
  setShowDuplicateNameError,
  setAddressInputMode,
  resetAddress,
  setAutocompletedAddress,
  setAddress,
  setIsMapboxTokenActive,
} from '../store/actions';

const GeneralDetails = () => {
  const { T } = useLocale(({ locale }) =>
    require(`../../../i18n/${locale}.json`)
  );
  const { store, dispatch } = useStore(CategorizedContentStoreContext);
  const { data, ui } = store;

  // compute dates with min and max range
  const startDate = createDateFromISOString(data.startDate);
  const endDate = createDateFromISOString(data.endDate);
  const maxStartDate = computeDateDiffDays(endDate, -1);
  const minEndDate = computeDateDiffDays(startDate, 1);

  // compute duplicate project name error message
  const projectNameErrorMessage = ui.showDuplicateNameError
    ? T.translate('EDIT_PROJECT_FORM_NAME_DUPLICATE_ERROR')
    : null;

  // event handlers section
  const handleProjectNameChange = (event) => {
    dispatch(setProjectName(event.target.value));

    if (ui.showDuplicateNameError) {
      dispatch(setShowDuplicateNameError(false));
    }
  };

  const handleProjectNameBlur = (event) => {
    const name = event.target.value;

    if (/^\s|\s$/.test(name)) {
      dispatch(setProjectName(name.trim()));
    }
  };

  const handleAddressInputModeChange = (inputMode, reset = false) => {
    dispatch(setAddressInputMode(inputMode));
    if (reset) {
      dispatch(resetAddress());
    }
  };

  const handleAutocompletedAddressChange = (autocompletedAddress) => {
    if (autocompletedAddress) {
      dispatch(setAutocompletedAddress(autocompletedAddress));
    } else {
      dispatch(resetAddress());
    }
  };

  const handleAddressChange = (address, resetOldAddress = false) => {
    const newAddress = resetOldAddress
      ? {
          ...pickAddressProps(initialState.data.asMutable({ deep: true })),
          ...address,
        }
      : {
          ...pickAddressProps(data.asMutable({ deep: true })),
          ...address,
        };
    dispatch(setAddress(newAddress));
  };

  const handleStartDateChange = (date) => {
    dispatch(setStartDate(date ? formatDate(date) : null));
  };

  const handleEndDateChange = (date) => {
    dispatch(setEndDate(date ? formatDate(date) : null));
  };

  const setMapboxTokenActive = (isActive) => {
    dispatch(setIsMapboxTokenActive(isActive));
  };

  return (
    <CategoryWrapper
      description={T.translate('PROJECT_CATEGORY_GENERAL_DESCRIPTION')}
      title={T.translate('PROJECT_CATEGORY_GENERAL')}
    >
      <TextField
        autoFocus
        errorMessage={projectNameErrorMessage}
        isEditing={ui.isEditing}
        onBlur={handleProjectNameBlur}
        onChange={handleProjectNameChange}
        placeHolder={T.translate('EDIT_PROJECT_FORM_PLACEHOLDER_NAME')}
        required
        title={T.translate('PROJECT_NAME')}
        value={data.name}
      />
      <AddressField
        address={pickAddressProps(data.asMutable({ deep: true }))}
        autocompletedAddress={ui.autocompletedAddress}
        inputMode={ui.addressInputMode}
        isEditing={ui.isEditing}
        isMapboxTokenActive={ui.isMapboxTokenActive}
        mapBoxAccessToken={MAPBOX_TOKEN}
        onAddressChange={handleAddressChange}
        onAutocompletedAddressResult={handleAutocompletedAddressChange}
        onInputModeChange={handleAddressInputModeChange}
        setMapboxTokenActive={setMapboxTokenActive}
        title={T.translate('PROJECT_ADDRESS')}
      />
      <DateField
        date={startDate}
        isEditing={ui.isEditing}
        maxDate={maxStartDate}
        onChange={handleStartDateChange}
        title={T.translate('PROJECT_START_DATE')}
      />
      <DateField
        date={endDate}
        isEditing={ui.isEditing}
        minDate={minEndDate}
        onChange={handleEndDateChange}
        title={T.translate('PROJECT_END_DATE')}
      />
    </CategoryWrapper>
  );
};

export default GeneralDetails;
